import {
    isSketchArcObject,
    isSketchCircleObject,
    isSketchEllipseObject,
    isSketchLineObject,
    isSketchParabolaObject,
    isSketchPointObject,
    isSketchSlotObject,
    isSketchSplineObject,
    isSketchStyleSplineObject,
} from '../../mesh/guards';
import type {SketchGroup} from '../../mesh/sketch/SketchGroup';
import type {SketchDimensionSnapshot} from './SketchDimensionTypes';
import {resolveSketchDimensionAngleBusinessValue} from './utils';

/** 二维点。 */
interface MeasureVec2 {
    readonly x: number;
    readonly y: number;
}

/** 线段（起点 + 终点）。 */
interface MeasureLine {
    readonly start: MeasureVec2;
    readonly end: MeasureVec2;
}

/**
 * 尺寸测量数据源（按引用序号/实体 ID 读取当前几何）。
 *
 * 设计说明：测量分支逻辑是**唯一权威**（`measureSketchDimensionFromSource`），
 * 数据源有两种实现——solver 求解系统（驱动求解后读参数）与草图运行时对象
 * （拖拽/几何提交后按当前点位重测 driven 显示值）。曾经测量逻辑只活在 solver
 * 侧，导致无 driving 尺寸参与的普通拖拽后 driven 值无人重测、显示旧值。
 */
export interface SketchDimensionMeasureSource {
    /** 按引用序号读点局部坐标；不可解析返回 null。 */
    readonly readPoint: (index: number) => MeasureVec2 | null;
    /** 按引用序号读线段两端局部坐标；不可解析返回 null。 */
    readonly readLine: (index: number) => MeasureLine | null;
    /** 按实体 ID 读圆/弧半径；无半径自由度返回 null。 */
    readonly readRadius: (entityId: string) => number | null;
    /** 按实体 ID 读椭圆长半轴；无半轴自由度返回 null。 */
    readonly readEllipseRadiusX?: (entityId: string) => number | null;
    /** 按实体 ID 读抛物线顶点局部坐标；不可解析返回 null。 */
    readonly readParabolaApex?: (entityId: string) => MeasureVec2 | null;
    /** 按实体 ID 读样条当前总长；不可解析返回 null。 */
    readonly readSplineLength: (entityId: string) => number | null;
    /**
     * 按实体 ID 读弧当前弧长（路径长度链的曲线段）；不可解析返回 null。
     *
     * solver 数据源可省略（起止角不是求解参数，含弧路径的 driving 编译
     * 本就按未支持降级，测量只在运行时源发生）。
     */
    readonly readArcLength?: (entityId: string) => number | null;
}

/** 点到无限直线的垂距幅值；线退化返回 null。 */
function pointLineDistance(point: MeasureVec2, line: MeasureLine): number | null {
    const dx = line.end.x - line.start.x;
    const dy = line.end.y - line.start.y;
    const length = Math.hypot(dx, dy);

    if (length < 1e-12) {
        return null;
    }

    return Math.abs((dx * (point.y - line.start.y) - dy * (point.x - line.start.x)) / length);
}

/** 两方向夹角幅值（弧度）。 */
function directionAngle(first: MeasureVec2, second: MeasureVec2): number {
    return Math.abs(Math.atan2(
        first.x * second.y - first.y * second.x,
        first.x * second.x + first.y * second.y,
    ));
}

/**
 * 按尺寸领域快照从数据源测量当前几何业务值（driven 显示值的唯一测量权威）。
 *
 * @param dimension 尺寸快照。
 * @param source 测量数据源。
 * @returns 业务测量值（核心单位，幅值）；类型不支持或引用不可解析返回 null。
 */
export function measureSketchDimensionFromSource(
    dimension: SketchDimensionSnapshot,
    source: SketchDimensionMeasureSource,
): number | null {
    /** 圆系引用 Min/Max 半径偏置和（测量值 = 锚点距离 + Σsign×r；Min=-1、Max=+1）。 */
    const arcConditionOffsetSum = (): number => dimension.references.reduce((sum, reference) => {
        if (reference.arcCondition !== 'min' && reference.arcCondition !== 'max') {
            return sum;
        }

        const radius = source.readRadius(reference.entityId);

        if (radius === null) {
            return sum;
        }

        return sum + (reference.arcCondition === 'min' ? -1 : 1) * radius;
    }, 0);
    const scale = dimension.parameter.valueScale;

    switch (dimension.dimensionKind) {
        case 'point-distance': {
            const first = source.readPoint(0);
            const second = source.readPoint(1);

            if (!first || !second) {
                return null;
            }

            const orientation = dimension.branch.orientation ?? 'aligned';

            if (orientation === 'horizontal') {
                return Math.abs(second.x - first.x) * scale;
            }
            if (orientation === 'vertical') {
                return Math.abs(second.y - first.y) * scale;
            }

            /** 链尺寸固定方向：距离取沿继承方向的投影（SW 链尺寸语义）。 */
            const fixedDirection = dimension.branch.fixedDirection;

            if (fixedDirection) {
                const length = Math.hypot(fixedDirection[0], fixedDirection[1]);

                if (length > 1e-12) {
                    return Math.abs(
                        ((second.x - first.x) * fixedDirection[0] + (second.y - first.y) * fixedDirection[1]) / length,
                    ) * scale;
                }
            }

            return (Math.hypot(second.x - first.x, second.y - first.y) + arcConditionOffsetSum()) * scale;
        }
        case 'length': {
            const line = source.readLine(0);

            if (!line) {
                return null;
            }

            /** H/V 尺寸命令固定方向语义（阶段 8）：两端点水平/竖直投影距离。 */
            const lengthOrientation = dimension.branch.orientation ?? 'aligned';

            if (lengthOrientation === 'horizontal') {
                return Math.abs(line.end.x - line.start.x) * scale;
            }
            if (lengthOrientation === 'vertical') {
                return Math.abs(line.end.y - line.start.y) * scale;
            }

            return Math.hypot(line.end.x - line.start.x, line.end.y - line.start.y) * scale;
        }
        case 'path-length': {
            // 路径总长（阶段 8）：链各段长度之和（线段直线长/弧段弧长/样条采样长）。
            let total = 0;

            for (let index = 0; index < dimension.references.length; index += 1) {
                const segment = source.readLine(index);

                if (segment) {
                    total += Math.hypot(segment.end.x - segment.start.x, segment.end.y - segment.start.y);
                    continue;
                }

                const entityId = dimension.references[index]?.entityId ?? '';
                const curveLength = source.readArcLength?.(entityId) ?? source.readSplineLength(entityId);

                if (curveLength === null) {
                    return null;
                }
                total += curveLength;
            }

            return total * scale;
        }
        case 'spline-length': {
            const entityId = dimension.references[0]?.entityId;
            const splineLength = entityId ? source.readSplineLength(entityId) : null;

            return splineLength === null ? null : splineLength * scale;
        }
        case 'point-line-distance': {
            const pointIndex = dimension.references[0]?.pointId ? 0 : 1;
            const point = source.readPoint(pointIndex);
            const line = source.readLine(pointIndex === 0 ? 1 : 0);

            if (!point || !line) {
                return null;
            }

            const distance = pointLineDistance(point, line);

            return distance === null ? null : (distance + arcConditionOffsetSum()) * scale;
        }
        case 'line-line-distance': {
            const firstLine = source.readLine(0);
            const secondLine = source.readLine(1);

            if (!firstLine || !secondLine) {
                return null;
            }

            const distance = pointLineDistance(secondLine.start, firstLine);

            return distance === null ? null : distance * scale;
        }
        case 'doubled-distance': {
            // 被测方（引用[0]：点或线起点）到中心线（引用[1]）的垂距 × 2（valueScale 承载）。
            const measured = source.readPoint(0) ?? source.readLine(0)?.start ?? null;
            const centerline = source.readLine(1);

            if (!measured || !centerline) {
                return null;
            }

            const distance = pointLineDistance(measured, centerline);

            return distance === null ? null : distance * scale;
        }
        case 'arc-length': {
            const centerIndex = dimension.references.findIndex((reference) => reference.topologyRole === 'center');
            const center = centerIndex >= 0 ? source.readPoint(centerIndex) : null;
            const endpoints = dimension.references
                .map((reference, index) => ({reference, index}))
                .filter(({reference, index}) => index !== centerIndex && Boolean(reference.pointId))
                .map(({index}) => source.readPoint(index));
            const start = endpoints[0] ?? null;
            const end = endpoints[1] ?? null;

            if (!center || !start || !end) {
                return null;
            }

            const startRadius = Math.hypot(start.x - center.x, start.y - center.y);
            const endRadius = Math.hypot(end.x - center.x, end.y - center.y);

            if (startRadius < 1e-12 || endRadius < 1e-12) {
                return null;
            }

            const branchSign = dimension.branch.directionSign ?? 1;
            let sweep = branchSign === 1
                ? Math.atan2(end.y - center.y, end.x - center.x) - Math.atan2(start.y - center.y, start.x - center.x)
                : Math.atan2(start.y - center.y, start.x - center.x) - Math.atan2(end.y - center.y, end.x - center.x);

            sweep %= Math.PI * 2;
            if (sweep <= 0) {
                sweep += Math.PI * 2;
            }

            return ((startRadius + endRadius) / 2) * sweep * scale;
        }
        case 'symmetric-angle':
        case 'angle': {
            const firstLine = source.readLine(0);
            const secondLine = source.readLine(1);

            if (!firstLine || !secondLine) {
                return null;
            }

            const geometricAngle = directionAngle(
                {x: firstLine.end.x - firstLine.start.x, y: firstLine.end.y - firstLine.start.y},
                {x: secondLine.end.x - secondLine.start.x, y: secondLine.end.y - secondLine.start.y},
            );

            return resolveSketchDimensionAngleBusinessValue(
                geometricAngle,
                scale,
                dimension.branch.angleQuadrant,
            );
        }
        case 'three-point-angle': {
            const vertex = source.readPoint(0);
            const firstRay = source.readPoint(1);
            const secondRay = source.readPoint(2);

            if (!vertex || !firstRay || !secondRay) {
                return null;
            }

            return directionAngle(
                {x: firstRay.x - vertex.x, y: firstRay.y - vertex.y},
                {x: secondRay.x - vertex.x, y: secondRay.y - vertex.y},
            ) * scale;
        }
        case 'radius':
        case 'diameter': {
            const entityId = dimension.references[0]?.entityId ?? '';
            const radius = source.readRadius(entityId);

            return radius === null ? null : radius * scale;
        }
        case 'curve-offset-distance': {
            // 圆/弧：|r₂ − r₁|；椭圆：|rx₂ − rx₁|（等距构造保证两半轴差同幅值）
            const firstEntityId = dimension.references[0]?.entityId ?? '';
            const secondEntityId = dimension.references[1]?.entityId ?? '';
            const firstRadius = source.readRadius(firstEntityId);
            const secondRadius = source.readRadius(secondEntityId);

            if (firstRadius !== null && secondRadius !== null) {
                return Math.abs(secondRadius - firstRadius) * scale;
            }

            const firstAxis = source.readEllipseRadiusX?.(firstEntityId) ?? null;
            const secondAxis = source.readEllipseRadiusX?.(secondEntityId) ?? null;

            if (firstAxis !== null && secondAxis !== null) {
                return Math.abs(secondAxis - firstAxis) * scale;
            }

            // 抛物线对：|apex₂ − apex₁|（等距构造保证两抛物线同轴、等焦距）
            const firstParabolaApex = source.readParabolaApex?.(firstEntityId) ?? null;
            const secondParabolaApex = source.readParabolaApex?.(secondEntityId) ?? null;

            if (firstParabolaApex && secondParabolaApex) {
                return Math.hypot(
                    secondParabolaApex.x - firstParabolaApex.x,
                    secondParabolaApex.y - firstParabolaApex.y,
                ) * scale;
            }

            // 样条对：距离由派生同步服务强制执行，测量口径 = 名义值
            return dimension.parameter.nominalValue * scale;
        }
        default:
            return null;
    }
}

/**
 * 构造草图运行时测量数据源（拖拽/几何提交后按当前点位重测 driven 显示值）。
 *
 * 与 solver 数据源的差异：直接读运行时对象局部坐标，天然覆盖**跨 island 引用**
 * （比如跨形状的链尺寸——两个形状没有共享约束时分属两个 island，solver 单岛
 * 测量拿不到对端点）。
 *
 * @param dimension 尺寸快照。
 * @param sketchGroup 草图运行时组。
 * @returns 测量数据源。
 */
export function createSketchRuntimeMeasureSource(
    dimension: SketchDimensionSnapshot,
    sketchGroup: SketchGroup,
): SketchDimensionMeasureSource {
    const resolve = (id: string | undefined | null): unknown => (
        id ? sketchGroup.getObjectByPersistentId(id) ?? null : null
    );

    return {
        readPoint: (index) => {
            const target = resolve(dimension.references[index]?.pointId);

            if (!isSketchPointObject(target)) {
                return null;
            }

            const local = target.getLocalPosition2D();

            return {x: local.x, y: local.y};
        },
        readLine: (index) => {
            const target = resolve(dimension.references[index]?.entityId);

            if (!isSketchLineObject(target)) {
                return null;
            }

            const start = target.startPoint.getLocalPosition2D();
            const end = target.endPoint.getLocalPosition2D();

            return {start: {x: start.x, y: start.y}, end: {x: end.x, y: end.y}};
        },
        readRadius: (entityId) => {
            const target = resolve(entityId);

            if (isSketchCircleObject(target)) {
                return target.radius;
            }
            if (isSketchArcObject(target)) {
                return target.getArcDefinition().radius;
            }
            // 圆弧槽口中心弧半径 = 弧心到中心弧中点距离（与注记锚点解析同款）。
            if (isSketchSlotObject(target) && target.isArcSlot()) {
                const centerPoint = target.arcCenterPoint;
                const midpoint = target.centerArcMidpoint;

                if (!centerPoint || !midpoint) {
                    return null;
                }

                const center = centerPoint.getLocalPosition2D();
                const mid = midpoint.getLocalPosition2D();

                return Math.hypot(mid.x - center.x, mid.y - center.y);
            }

            return null;
        },
        readEllipseRadiusX: (entityId) => {
            const target = resolve(entityId);

            if (!isSketchEllipseObject(target)) {
                return null;
            }

            return target.getEllipseDefinition().radiusX;
        },
        readParabolaApex: (entityId) => {
            const target = resolve(entityId);

            if (!isSketchParabolaObject(target)) {
                return null;
            }

            const apex = target.getApexPoint().getLocalPosition2D();

            return {x: apex.x, y: apex.y};
        },
        readArcLength: (entityId) => {
            const target = resolve(entityId);

            if (!isSketchArcObject(target)) {
                return null;
            }

            const definition = target.getArcDefinition();

            return definition.radius * Math.abs(definition.endAngle - definition.startAngle);
        },
        readSplineLength: (entityId) => {
            const target = resolve(entityId);

            if (!isSketchSplineObject(target) && !isSketchStyleSplineObject(target)) {
                return null;
            }

            const points = target.getCurvePoints().map((point) => target.referencePlane.worldToLocal(point.clone()));
            let total = 0;

            for (let index = 1; index < points.length; index += 1) {
                total += Math.hypot(points[index]!.x - points[index - 1]!.x, points[index]!.y - points[index - 1]!.y);
            }

            return points.length >= 2 ? total : null;
        },
    };
}
